import {useQueries, useQueryClient, type UseQueryOptions, type UseQueryResult} from '@tanstack/react-query'
import {useMemo} from 'react'

import {apiGetPaginatedItems} from '../../../api/memex-items/api-get-paginated-items'
import {createMemexItemModel} from '../../../models/memex-item-model'
import {setQueryDataForMemexItemsPage} from '../query-client-api'
import type {GroupId, PaginatedMemexItemsQueryKey} from './query-keys'
import {buildMemexItemsPageQueryKey, groupIdForUngroupedData} from './query-keys'
import {
  type ActiveQueriesMetadata,
  type MemexItemGroupsPageQueryData,
  type MemexItemsPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  type PaginatedMemexItemsQueryVariables,
} from './types'
import {useMemexItemsActiveQueriesMetadata} from './use-memex-items-active-queries-metadata'

type UsePaginatedMemexItemsActiveQueriesReturnType = {
  queryKeys: Array<PaginatedMemexItemsQueryKey>
  queries: Array<UseQueryResult<MemexItemsPageQueryData | MemexItemGroupsPageQueryData>>
}

/**
 * Based on data stored in the `activeQueriesMetadata` query, this hook
 * builds up a list of options to pass to `useQueries`, so that we can dynamically
 * monitor multiple queries representing memex items data.
 * @param variables PaginatedMemexItemsQueryVariables to use in QueryKey
 * @returns UsePaginatedMemexItemsActiveQueriesReturnType
 */
export function usePaginatedMemexItemsActiveQueries(
  variables: PaginatedMemexItemsQueryVariables,
): UsePaginatedMemexItemsActiveQueriesReturnType {
  const {activeQueriesMetadata, setActiveQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const queryClient = useQueryClient()

  // Build up a set of options to pass to useQueries
  const queries = useMemo(() => {
    const options: Array<UseQueryOptions<MemexItemsPageQueryData | MemexItemGroupsPageQueryData>> = []
    // This will be a flat list of query keys (in this case just the groupId + pageIndex pairs) for
    // all queries/pages that we want to monitor.
    const queryKeys = buildQueryKeysFromActiveQueriesMetadata(activeQueriesMetadata)
    for (const {groupId, pageParam} of queryKeys) {
      options.push({
        queryKey: buildMemexItemsPageQueryKey(variables, groupId, pageParam),
        queryFn: async () => {
          const response = await apiGetPaginatedItems({...variables, ...pageParam, groupedByGroupId: groupId})
          // How we handle the response, will depend on if the data is gropued or not.
          if ('groups' in response) {
            // If we have multiple groups in the response, then that means we have made a request
            // with a query key that represents multiple groups. We need to manually set up the query data
            // for each group, as well as the active queries metadata for each of the new groups in the response.
            const oldGroups = 'groups' in activeQueriesMetadata ? activeQueriesMetadata.groups : []
            setActiveQueriesMetadata({
              groups: [
                ...oldGroups,
                ...response.groups.map(g => ({groupId: g.groupId, pageParams: [pageParamForInitialPage]})),
              ],
              pageParams: activeQueriesMetadata.pageParams,
            })

            // Iterate over groups in response and manually add to query data
            for (const group of response.groups) {
              setQueryDataForMemexItemsPage(queryClient, variables, group.groupId, pageParamForInitialPage, {
                nodes: group.items.nodes.map(item => createMemexItemModel(item)),
                pageInfo: group.items.pageInfo,
              })
            }
            return {
              groups: response.groups.map(g => ({groupId: g.groupId, groupName: g.groupName})),
              pageInfo: response.pageInfo,
            }
          } else {
            // If our data is ungrouped, then we can just return the response as-is.
            return {...response, nodes: response.nodes.map(item => createMemexItemModel(item))}
          }
        },
        structuralSharing: false,
        staleTime: Infinity,
      })
    }
    return options
  }, [activeQueriesMetadata, queryClient, setActiveQueriesMetadata, variables])

  // The queries that are returned by `useQueries` do not have any information about their own query keys,
  // so we return the queryKeys alongside them so that downstream consumers can map back and forth by index
  return {
    queries: useQueries({
      queries,
    }),
    queryKeys: queries.map(q => (q.queryKey as PaginatedMemexItemsQueryKey) || []),
  }
}

/**
 * Builds a set of query keys based on an ActiveQueriesMetadata object.
 * For ungrouped data, this is just an array of {groupId: groupIdForUngroupedData, pageParam: pageParam} for each pageParam
 * in our ActiveQueriesMetadata.
 * For grouped data, we iterate over each group and for each group we iterate over that groups pageParams. Then we return
 * {groupId: group.id, pageParam: pageParam} for each pageParam in each group, as well as
 * iterating over the pageParams for the groups as a whole.
 */
function buildQueryKeysFromActiveQueriesMetadata(
  activeQueriesMetadata: ActiveQueriesMetadata,
): Array<{groupId: GroupId; pageParam: PageParam}> {
  if ('groups' in activeQueriesMetadata) {
    const queryKeysForItems = activeQueriesMetadata.groups.flatMap(group =>
      group.pageParams.map(pageParam => ({groupId: group.groupId, pageParam})),
    )
    const queryKeysForGroups = activeQueriesMetadata.pageParams.map(pageParam => ({
      groupId: groupIdForUngroupedData,
      pageParam,
    }))
    return [...queryKeysForItems, ...queryKeysForGroups]
  } else {
    return activeQueriesMetadata.pageParams.map(pageParam => ({groupId: groupIdForUngroupedData, pageParam}))
  }
}
